import type { WcPosId } from './types';

/** Single World Cup format: a 15-player squad (11 starters + 4 subs), budget
 *  capped, at most WC_COUNTRY_LIMIT players from any one country. Mirrors FPL's
 *  11-a-side shape (2-5-5-3 squad, 1-4-4-2 default) with country replacing club. */
export const WC_STARTERS = 11;
export const WC_BENCH = 4;
export const WC_SQUAD_SIZE = WC_STARTERS + WC_BENCH; // 15
export const WC_COUNTRY_LIMIT = 3;
/** Max players per country for a given gameweek — relaxes through the late
 *  knockouts: Quarter-finals (GW6) 5, Semi-finals (GW7) 6, Final (GW8) 8; the
 *  base 3 everywhere earlier. Enforced against the round you're building for. */
export function wcCountryLimit(gwIndex: number | null | undefined): number {
  if (gwIndex === 6) return 5;
  if (gwIndex === 7) return 6;
  if (gwIndex != null && gwIndex >= 8) return 8;
  return WC_COUNTRY_LIMIT;
}
/** Nations still alive going into a given gameweek — restricts the transfer-in
 *  pool to players who can actually still score, keyed by api-football national-
 *  team id. Semi-finals (GW7): France (2), Spain (9), Argentina (26), England
 *  (10). Returns null when there's no restriction (group stage + earlier
 *  knockouts, where every nation is still selectable). Enforced against the
 *  round you're building for; it only governs who you can transfer IN — players
 *  already in your squad from knocked-out nations are left untouched. The
 *  Final's two teams aren't known yet, so GW8 stays unrestricted for now. */
export function wcTransferTeams(gwIndex: number | null | undefined): Set<number> | null {
  if (gwIndex === 7) return new Set([2, 9, 26, 10]); // FRA, ESP, ARG, ENG
  return null;
}

/** £m budget cap (Princhess default; FWC also 150 via its mount override). */
export const WC_BUDGET_CAP = 150;
export const CAPTAIN_MULTIPLIER = 2;

export const WC_POSITIONS: WcPosId[] = ['GK', 'DEF', 'MID', 'FWD'];
export const POS_LABEL: Record<WcPosId, string> = { GK: 'Goalkeeper', DEF: 'Defender', MID: 'Midfielder', FWD: 'Forward' };
export const POS_PLURAL: Record<WcPosId, string> = { GK: 'goalkeepers', DEF: 'defenders', MID: 'midfielders', FWD: 'forwards' };

/** Allowed counts per position among the 11 starters. */
export const FORMATION: Record<WcPosId, { min: number; max: number }> = {
  GK: { min: 1, max: 1 },
  DEF: { min: 3, max: 5 },
  MID: { min: 3, max: 5 }, // ≥3 midfielders → valid set is 5-4-1/5-3-2/4-5-1/4-4-2/4-3-3/3-5-2/3-4-3
  FWD: { min: 1, max: 3 },
};

/** Fixed total squad composition (starters + bench). */
export const SQUAD_COMP: Record<WcPosId, number> = { GK: 2, DEF: 5, MID: 5, FWD: 3 };

/** Skeleton shown when no players are picked. */
export const DEFAULT_START: Record<WcPosId, number> = { GK: 1, DEF: 4, MID: 4, FWD: 2 };

export const KIT_COLORS: Array<{ name: string; hex: string }> = [
  { name: 'Red', hex: '#e01b22' }, { name: 'Blue', hex: '#1f49e0' }, { name: 'Sky', hex: '#5ca8e6' },
  { name: 'Navy', hex: '#0e1b4d' }, { name: 'White', hex: '#f4f4f4' }, { name: 'Black', hex: '#16181d' },
  { name: 'Yellow', hex: '#f5d017' }, { name: 'Green', hex: '#15a34a' }, { name: 'Orange', hex: '#f07a18' },
  { name: 'Pink', hex: '#f06292' }, { name: 'Caramel', hex: '#b5793b' },
];
export const DEFAULT_PRIMARY = '#1f49e0';
export const DEFAULT_SECONDARY = '#f4f4f4';

/** Per-country placeholder colour, keyed by api-football national-team id. Used
 *  as the avatar background behind a player's initials when no headshot exists
 *  (nicer than api-sports' generic grey silhouette). Tuned to each nation's
 *  home-kit primary, slightly muted so white text stays legible; white-kit
 *  nations (England, Germany, USA, the Gulf/North-African whites, etc.) fall back
 *  to a legible secondary. Unknown ids → DEFAULT_PRIMARY. */
export const COUNTRY_COLORS: Record<number, string> = {
  1532: '#1B7A47', // Algeria
  26: '#6CA6D9', // Argentina
  20: '#0E8A3E', // Australia
  775: '#C8102E', // Austria
  1: '#A4161A', // Belgium
  1113: '#15489A', // Bosnia & Herzegovina
  6: '#E8C200', // Brazil
  5529: '#C8102E', // Canada
  1533: '#1A56B0', // Cape Verde Islands
  8: '#E0B400', // Colombia
  1508: '#1769C7', // Congo DR
  3: '#C8102E', // Croatia
  5530: '#1E63C4', // Curaçao
  770: '#B01828', // Czech Republic
  2382: '#E0B000', // Ecuador
  32: '#B01818', // Egypt
  10: '#D4D6DB', // England (white kit → soft grey-white)
  2: '#2A4C8F', // France
  25: '#2E2E32', // Germany (white kit → off-black)
  1504: '#1B7A47', // Ghana
  2386: '#1E50A8', // Haiti
  22: '#C8102E', // Iran (white kit → red secondary)
  1567: '#1B7A47', // Iraq (white kit → green secondary)
  1501: '#E06A1A', // Ivory Coast
  12: '#1B2C66', // Japan
  1548: '#B01818', // Jordan (white kit → red secondary)
  16: '#1A6E3A', // Mexico
  31: '#B22222', // Morocco
  1118: '#E36B1E', // Netherlands
  4673: '#2E2E32', // New Zealand (white kit → off-black)
  1090: '#B81B2C', // Norway
  11: '#B01828', // Panama
  2380: '#C8102E', // Paraguay
  27: '#7A1F2B', // Portugal (maroon)
  1569: '#7A1230', // Qatar (maroon)
  23: '#1B7A47', // Saudi Arabia (white kit → green secondary)
  1108: '#1B2C66', // Scotland
  13: '#1B7A47', // Senegal (white kit → green secondary)
  1531: '#1A8A4A', // South Africa
  17: '#C8102E', // South Korea
  9: '#B01828', // Spain
  5: '#E0B000', // Sweden
  15: '#C8102E', // Switzerland
  28: '#B01818', // Tunisia (white kit → red secondary)
  777: '#C8102E', // Türkiye
  2384: '#1B2C66', // USA (white kit → navy secondary)
  7: '#4B9CD3', // Uruguay
  1568: '#1A8A4A', // Uzbekistan (white kit → green secondary)
};

/** Placeholder avatar colour for a country (api-football team id). */
export function countryColor(teamId: number): string {
  return COUNTRY_COLORS[teamId] ?? DEFAULT_PRIMARY;
}

export function formatBudget(m: number): string {
  return `£${m.toFixed(1)}m`;
}
